/*
 * whatWeDo
 * --------
 * Single source of truth for the "What We Do" service catalog.
 *
 * Consumed by:
 *   - `SidebarDrawer.tsx`   — populates the mega-menu sub-item list +
 *                              per-item preview media in col 3.
 *   - `ServiceDetail.tsx`   — drives the full-page video hero, hero-
 *                              card copy, breadcrumbs, and CTA.
 *   - `Contact.tsx`         — SERVICE_OPTIONS is derived from this
 *                              list so the dropdown, the sidebar, and
 *                              every detail page stay in lock-step.
 *
 * The `id` doubles as both the URL slug (`/services/<id>`) and the
 * value emitted by the Contact form's service dropdown — keeping the
 * two symmetrical means `Connect with our experts` on any detail page
 * just needs to link to `/contact?service=<id>` and everything wires
 * up automatically.
 */

// Shared cover asset URLs — several services intentionally reuse the
// same underlying video (Artificial Intelligence + AI/GenAI, Cloud/Sec
// + Cybersecurity, Product & Platforms + Application Services). Named
// constants make the reuse explicit and give us a single place to swap
// re-encodes.
export const WHAT_WE_DO_MEDIA = {
  itConsulting:
    'https://istatec-assets.istatec.com/istatec_it_consulting_cover.webm',
  aiGenai:
    'https://istatec-assets.istatec.com/ai_genai_automations_cover.webm',
  salesforce:
    'https://istatec-assets.istatec.com/salesforce_automation_cover.webm',
  cybersecurity:
    'https://istatec-assets.istatec.com/cybersecurity_automation_cover.webm',
  applicationServices:
    'https://istatec-assets.istatec.com/application_services_cover.webm',
  dataEngineering:
    'https://istatec-assets.istatec.com/data_engineering_and_analytics_cover.webm',
  marketing:
    'https://istatec-assets.istatec.com/marketing_and_engagement.webm',
  qualityIntelligence:
    'https://istatec-assets.istatec.com/quality_inteligane_cover.webm',
  whatWeDoOverview:
    'https://istatec-assets.istatec.com/Our_Excelent_Services_For_Clint_To_transform.webm',
} as const;

export interface WhatWeDoService {
  /**
   * URL slug (`/services/<id>`) AND the id used by the Contact form's
   * SERVICE_OPTIONS. Keeping them symmetrical is what makes the
   * `?service=<id>` pre-fill on the contact CTA just work.
   */
  id: string;
  /** Display title — sidebar label, hero heading, form option text. */
  title: string;
  /**
   * Short (1-2 sentence) description used in the sidebar drawer's
   * preview column — needs to read well at ~15-16px body copy.
   */
  sidebarTagline: string;
  /**
   * Longer, marketing-tone description shown inside the hero card on
   * the detail page. Written in "We do X to achieve Y" voice so the
   * copy feels like it's coming from the team, not a spec sheet.
   */
  heroDescription: string;
  /** Cover video URL (webm). */
  mediaUrl: string;
  /**
   * Label for the primary CTA button on the detail page. Defaults to
   * "Connect with our experts" for every service except AI, which is
   * "Do your AI Readiness Assessment" per the client's ask.
   */
  ctaLabel: string;
  /**
   * Optional query fragment appended to `/contact?service=<id>` so
   * specific CTAs can pre-fill the message body / route to a
   * particular inquiry. Currently only AI uses this
   * (`inquiry=ai-readiness`) to prefill an assessment-request note.
   */
  ctaInquiry?: string;
  /**
   * Service-specific paragraph rendered under "How we work with you"
   * on the detail page. Focuses on the CLIENT's experience of
   * starting an engagement with us on this specific service — what
   * the discovery looks like, what "first slice" means for this
   * discipline, how the team engages. Written per service so every
   * detail page reads as tailored, not templated.
   */
  approachParagraph: string;
  /**
   * Service-specific paragraph rendered under "Why we keep learning,
   * researching, and innovating" on the detail page. Focuses on the
   * ongoing value the CLIENT gets from working with a team that
   * stays plugged into the state of the art for this specific
   * service (e.g. Salesforce releases, AI model churn, cloud
   * primitives).
   */
  learningParagraph: string;
  /**
   * Optional list of concrete named sub-offerings the service ships.
   * Each has a short human-voiced description explaining what the
   * offering is and what a client actually gets. Rendered on the
   * detail page as a full-bleed grid on a deep-navy background,
   * mirroring the sub-offerings pattern common on enterprise
   * consultancy pages (Zensar, Accenture Industry X, etc.).
   *
   * Only rendered when defined and non-empty — services without a
   * list simply skip the section.
   */
  subOfferings?: readonly SubOffering[];
}

/**
 * A single named offering shown in the "Our sub-offerings" grid on a
 * service detail page. Names should be short (2-4 words) and
 * production-quality; descriptions are 1-2 sentences focused on what
 * the client actually gets, not a marketing pitch.
 */
export interface SubOffering {
  name: string;
  description: string;
}

export const WHAT_WE_DO_SERVICES: readonly WhatWeDoService[] = [
  {
    id: 'it-consultancy-augmentation',
    title: 'IT Consultancy & Augmentation',
    sidebarTagline:
      "Senior IT advisors plus best-sourced expert engineering pods you can plug in overnight — from architecture reviews, cloud modernization, AI/GenAI enablement, and platform strategy to fully-managed augmentation squads that scale with your roadmap. We build alongside your team from discovery through delivery, without breaking the momentum you've already got.",
    heroDescription:
      'We embed senior advisors and best-sourced engineering pods into your team to accelerate architecture, cloud, AI/GenAI, and platform work — plugging into your delivery rhythm and scaling capacity with your roadmap.',
    mediaUrl: WHAT_WE_DO_MEDIA.itConsulting,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Consultancy work with us starts with a scoping call — a real one, not a sales pitch. We look at what you're trying to do, what you've already tried, and where the gaps actually are. Then we agree on a small first engagement that gets senior people onto your problem within weeks, not quarters.",
    learningParagraph:
      "You benefit from a team that stays plugged into how architecture and cloud tooling actually evolve. When a new pattern is worth adopting, we bring it into your engagement — you don't have to run your own tech radar or wait for the next hiring round to get access to it.",
    subOfferings: [
      {
        name: 'Architecture Reviews & Advisory',
        description:
          'Deep-dive audits of your current architecture with concrete, prioritized recommendations. Senior engineers on the call, not slide-deck consultants.',
      },
      {
        name: 'Senior Engineering Pods',
        description:
          "Ready-to-plug expert pods that own delivery end-to-end. Small teams, direct access, and the same people you interviewed \u2014 no bait-and-switch after the SOW is signed.",
      },
      {
        name: 'Cloud & Platform Modernization',
        description:
          "Incremental migrations that ship value in weeks, not multi-year rewrites. We measure risk continuously and roll back cleanly if the numbers don\u2019t hold up.",
      },
      {
        name: 'Fractional Tech Leadership',
        description:
          'A senior technical lead embedded with your team for the hours you actually need — architecture calls, hiring interviews, board-review prep.',
      },
      {
        name: 'Delivery Assurance',
        description:
          "Independent review of an ongoing project \u2014 code quality, delivery risk, team health. Findings your team can act on, not a slide deck for the C-suite.",
      },
    ],
  },
  {
    id: 'ai-genai-automations',
    title: 'AI / GenAI Automations',
    sidebarTagline:
      'Production-grade AI and GenAI automations that ship — LLM agents, RAG pipelines, workflow copilots, and computer-vision or document-extraction bots wired straight into your existing stack. We handle evaluation, guardrails, and MLOps from day one so the systems stay accurate, safe, and cost-controlled as they scale.',
    heroDescription:
      'We design and ship production-grade AI and GenAI automations — from LLM agents and RAG pipelines to workflow copilots and document-extraction bots — with the guardrails, evaluation, and MLOps that keep them accurate, safe, and cost-controlled at scale.',
    mediaUrl: WHAT_WE_DO_MEDIA.aiGenai,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "AI work with us starts narrow. We pick one specific process, one clear success signal, and build a proof-of-concept your team can pressure-test in weeks. Once we've proven the pattern works on your data and your users, we scale it — nothing goes to production without evaluation, guardrails, and a real business case behind it.",
    learningParagraph:
      "The AI space moves weekly. Our ongoing evaluation and research means your production systems get swapped onto better models and cleaner patterns as they emerge — you get the improvements without having to rebuild what you've already shipped or hire a team just to keep up with the field.",
    subOfferings: [
      {
        name: 'Document Intelligence',
        description:
          'Extract structured data from contracts, invoices, forms, and unstructured PDFs at production-grade accuracy. Includes a review workflow for the edge cases the model gets wrong.',
      },
      {
        name: 'Conversational AI',
        description:
          "Assistants that speak the language of your business \u2014 grounded in your data, wired into your tools, and guardrailed against off-topic drift.",
      },
      {
        name: 'Agentic AI',
        description:
          'Multi-step agents that plan, act, and self-check across your systems. Human-in-the-loop by default; fully autonomous once the pattern earns it.',
      },
      {
        name: 'AI Guardrails',
        description:
          'Evaluation harnesses, content filtering, prompt-injection defenses, and audit trails — the infrastructure that makes AI safe to put in front of real users.',
      },
      {
        name: 'Multi-Modal Generative AI',
        description:
          'Systems that reason across text, images, audio, and video — for RFP intake, product search, media analysis, and content generation.',
      },
      {
        name: 'RAG Pipelines',
        description:
          "Retrieval-augmented generation grounded in your knowledge base with citations, evaluation, and freshness monitoring \u2014 no hallucinated answers landing in customer chat.",
      },
      {
        name: 'Enterprise AI Engineering',
        description:
          'The production plumbing behind AI: model registries, feature stores, evaluation harnesses, observability, and rollback strategy — so AI ships and stays shipped.',
      },
      {
        name: 'Workflow Copilots',
        description:
          'In-app copilots that live inside your existing tools (Salesforce, Jira, Confluence, custom apps) and shorten common workflows without another tab to switch to.',
      },
    ],
  },
  {
    id: 'salesforce-automation',
    title: 'Salesforce Automation',
    sidebarTagline:
      'Turn your Salesforce org into a growth engine — flows, integrations, and agentic assistants that shorten every deal cycle and connect Sales, Service, and Marketing Cloud into one revenue system.',
    heroDescription:
      'We turn Salesforce into a growth engine — connecting Sales, Service, and Marketing Cloud with flows, integrations, and agentic assistants that shorten every deal cycle and keep teams focused on customer outcomes.',
    mediaUrl: WHAT_WE_DO_MEDIA.salesforce,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Salesforce work with us starts inside your org. We map how Sales, Service, and Marketing Cloud are actually being used today, spot the biggest workflow drags, and ship the first automation in weeks. Once the pattern's working, we roll it across the org — small wins first, big migrations only when they've earned it.",
    learningParagraph:
      "Salesforce ships three major releases a year and the platform surface keeps growing. We stay on top of what's actually useful in each drop — Flow enhancements, Agentforce updates, new integration options — and pick up the improvements that matter for your org without you having to plan the upgrade path.",
    subOfferings: [
      {
        name: 'Sales Cloud Implementation',
        description:
          "End-to-end Sales Cloud setup or migration \u2014 pipeline modeling, forecast configuration, role hierarchy, and integration with the rest of your stack.",
      },
      {
        name: 'Service Cloud & Case Management',
        description:
          "Case routing, agent workspaces, knowledge management, and CTI integration \u2014 so every ticket lands with the right person and closes faster.",
      },
      {
        name: 'Marketing Cloud Integration',
        description:
          'Connect Marketing Cloud with Sales & Service so campaigns, leads, and cases flow through one unified customer view — not three disconnected reports.',
      },
      {
        name: 'Custom Flows & Apex',
        description:
          "Complex business logic in Flow, Apex triggers, and Lightning Web Components \u2014 code your team can maintain, not a black-box managed package with expiring keys.",
      },
      {
        name: 'Agentforce Integration',
        description:
          "AI-powered agents native to Salesforce, wired into your data and processes. Deployed to your existing users under the same permissions and audit trail.",
      },
      {
        name: 'Data Cloud & CRM Analytics',
        description:
          'Unify customer data across sources into Salesforce Data Cloud, then surface real-time insights through CRM Analytics and Einstein.',
      },
      {
        name: 'Managed Salesforce Ops',
        description:
          "Ongoing admin + development capacity so your org keeps evolving between quarterly releases. Predictable pricing, SLA-backed response, no lock-in on renewal.",
      },
    ],
  },
  {
    id: 'cybersecurity-automation',
    title: 'Cybersecurity Automation',
    sidebarTagline:
      'Respond at machine speed with automated detection, playbook-driven response, identity governance, and continuous compliance — a security posture that scales with your business instead of your alert queue.',
    heroDescription:
      'We help you respond at machine speed — automating threat detection, playbook-driven response, identity governance, and continuous compliance so your security posture scales with your business, not with your alert queue.',
    mediaUrl: WHAT_WE_DO_MEDIA.cybersecurity,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Security work with us starts with the threat model. We look at your attack surface, your recent incidents, and your compliance posture, then pick the first automation that would meaningfully move the needle on your alert queue. Playbooks go live in weeks, response times start coming down, and we scale coverage from there.",
    learningParagraph:
      "Attackers keep evolving; so do we. Continuous review of new detection techniques, response playbooks, and identity patterns means your security posture stays ahead of what's coming — you don't wait for the next audit or breach to find out you've fallen behind.",
    subOfferings: [
      {
        name: 'Automated Threat Detection',
        description:
          'SIEM rules, XDR playbooks, and behavioural analytics tuned to your environment — not off-the-shelf noise. Real signal, low false-positive rate.',
      },
      {
        name: 'Response Playbook Automation',
        description:
          'Automated response workflows for the top incident types in your alert queue. Analysts intervene by exception, not by default.',
      },
      {
        name: 'Identity Governance',
        description:
          'Automated access reviews, birthright provisioning, and least-privilege enforcement across your identity providers, cloud accounts, and SaaS.',
      },
      {
        name: 'Continuous Compliance',
        description:
          'Ongoing SOC 2, ISO 27001, HIPAA, or PCI compliance monitoring — auto-collected evidence, drift alerts, and audit-ready reports on demand.',
      },
      {
        name: 'Vulnerability Management',
        description:
          "Prioritized remediation based on real exploitability, not just CVSS. We integrate with your ticketing so fixes land in your normal sprint cadence.",
      },
      {
        name: 'Security Posture Reviews',
        description:
          'Quarterly reviews of your security posture with concrete, prioritized recommendations. Honest findings, not a fear-driven upsell.',
      },
    ],
  },
  {
    id: 'application-services',
    title: 'Application Services',
    sidebarTagline:
      'Manage, modernize, and scale apps with services that enhance performance, usability, and integration across the enterprise.',
    heroDescription:
      'We streamline, maintain, and re-engineer applications to create highly functional business units.',
    mediaUrl: WHAT_WE_DO_MEDIA.applicationServices,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Application work with us starts with an assessment. We look at your current portfolio — what's healthy, what's dragging, what's actually blocking your team — and pick the highest-leverage modernization to start. First shippable improvement lands in weeks, not quarters, and every subsequent iteration builds on what your team has already validated.",
    learningParagraph:
      "Frameworks age, patterns evolve, and what was best-in-class three years ago is legacy today. Our ongoing research means your applications get the modernization they need in small increments — not through the painful, high-risk 'let's rebuild the whole thing' project that most teams learn to fear.",
    subOfferings: [
      {
        name: 'Application Development',
        description:
          "Full-stack development of new applications, from discovery through launch, with tight iteration cycles and continuous deployment from day one.",
      },
      {
        name: 'Application Modernization',
        description:
          "Legacy rewrites, refactoring, and re-platforming \u2014 small increments measured against your business KPIs, with clear rollback plans at every step.",
      },
      {
        name: 'API Design & Integration',
        description:
          'Well-designed REST or GraphQL APIs, event-driven integrations, and third-party system integrations — documented, tested, and versioned properly.',
      },
      {
        name: 'Application Management',
        description:
          "Ongoing support, patching, and enhancement of existing applications. Predictable pricing, SLA-backed response times, no lock-in on renewal.",
      },
      {
        name: 'Enterprise Change Management',
        description:
          "Rollout planning, stakeholder alignment, and user-adoption programs so your application investments actually deliver the change they promise.",
      },
      {
        name: 'Quality Engineering',
        description:
          'Test automation, performance testing, and continuous quality practices woven into delivery — not bolted on at the end of a release.',
      },
    ],
  },
  {
    id: 'artificial-intelligence',
    title: 'Artificial Intelligence',
    sidebarTagline:
      'By turning data into actionable insights, our AI solutions unlock growth, boost efficiency, and power intelligent decision-making.',
    heroDescription:
      'We harness AI to convert data into powerful insights that accelerate innovation, improve efficiency, and create new opportunities.',
    mediaUrl: WHAT_WE_DO_MEDIA.aiGenai,
    ctaLabel: 'Do your AI Readiness Assessment',
    ctaInquiry: 'ai-readiness',
    approachParagraph:
      "AI work with us starts with your data and your outcomes. We look at which decisions could genuinely be improved with better signal, prototype the model, and prove the lift on your actual data before anything ships to production. No demos, no synthetic benchmarks — real proof against your baseline, or we don't move forward.",
    learningParagraph:
      "AI research moves at a pace no single team can track alone. We do that tracking on your behalf — new model architectures, better training techniques, cheaper inference paths — and roll the useful advances into how we build for you, so your AI capability compounds year over year rather than freezing at the version we first shipped.",
    subOfferings: [
      {
        name: 'AI Readiness Assessment',
        description:
          "Structured evaluation of your organization\u2019s AI maturity \u2014 data, skills, culture, use-cases \u2014 with a prioritized roadmap you can start executing this quarter.",
      },
      {
        name: 'Optimization Engines',
        description:
          'Mathematical optimization for pricing, routing, staffing, and inventory. Real constraints, real cost functions, real ROI at the end.',
      },
      {
        name: 'Forecasting Models',
        description:
          'Time-series forecasting for revenue, inventory, capacity, and demand. Explains its predictions, quantifies uncertainty, and updates as new data lands.',
      },
      {
        name: 'Predictive Analytics',
        description:
          "Models that forecast churn, conversion, or risk \u2014 trained on your data, evaluated against your actual outcomes, and monitored in production.",
      },
      {
        name: 'Computer Vision',
        description:
          "Object detection, defect inspection, document parsing, and video analytics \u2014 from proof-of-concept to production pipelines with real monitoring.",
      },
      {
        name: 'Natural Language Understanding',
        description:
          "Extract intent, entities, sentiment, and structured data from customer conversations, reviews, and support tickets \u2014 grounded in your business context.",
      },
      {
        name: 'Recommendation Systems',
        description:
          "Personalized recommendations for e-commerce, content, or B2B product discovery \u2014 with cold-start handling and continuous re-training built in.",
      },
    ],
  },
  {
    id: 'cloud-infrastructure-security',
    title: 'Cloud, Infrastructure, and Security',
    sidebarTagline:
      'Transform operations with intelligent cloud, robust infrastructure, and secure-by-design solutions that build trust and scale value.',
    heroDescription:
      'We consult, build, connect, secure (engineer), and manage (engage) core, cloud, and edge with a clear focus on enhancing the end-client experience through measurable outcomes.',
    mediaUrl: WHAT_WE_DO_MEDIA.cybersecurity,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Cloud work with us starts with an architecture review. We look at what you're running today, spot the cost, reliability, and security gaps that matter most, and pick the first migration that would move real numbers. Small first step, measurable improvement inside weeks, then we scale the approach across the rest of your stack.",
    learningParagraph:
      "Cloud primitives, security frameworks, and networking patterns keep changing under everyone's feet. Our continuous investment in staying current means your infrastructure gets the newer, cheaper, safer building blocks as they land — without you having to run your own R&D program or read every AWS/Azure/GCP release note.",
    subOfferings: [
      {
        name: 'Cloud Migration & Modernization',
        description:
          "Lift-and-shift or refactor-and-move \u2014 we pick the strategy that fits each workload\u2019s actual value, not a one-size-fits-all migration plan.",
      },
      {
        name: 'Hybrid Cloud Operations',
        description:
          "Consistent operations across AWS, Azure, GCP, and on-prem infrastructure. Unified observability, common runbooks, and portable deployment patterns.",
      },
      {
        name: 'Cloud-Native Security',
        description:
          "Security engineered in from day one \u2014 IAM, encryption, network segmentation, and workload protection built to your compliance requirements.",
      },
      {
        name: 'Managed Cloud Operations',
        description:
          "24/7 managed operations for your cloud infrastructure with predictable SLAs. Incidents handled by engineers who know your architecture, not first-line ticket triagers.",
      },
      {
        name: 'Cloud Cost Optimization',
        description:
          "Ongoing cost reviews with concrete recommendations \u2014 right-sizing, reserved instances, savings plans, workload placement. Real savings, not vanity metrics.",
      },
      {
        name: 'Identity & Access Management',
        description:
          "Zero-trust identity architecture, SSO consolidation, and least-privilege enforcement across your cloud accounts and SaaS applications.",
      },
      {
        name: 'Infrastructure as Code',
        description:
          "Terraform, Pulumi, or CDK \u2014 version-controlled, tested, and reviewed like any other code. No more hand-configured production.",
      },
    ],
  },
  {
    id: 'data-engineering-analytics',
    title: 'Data Engineering and Analytics',
    sidebarTagline:
      'Drive smarter decisions with modern data practices — governance, AI, and products that turn insight into impact.',
    heroDescription:
      'We unlock insights that drive smarter business decisions by simplifying data complexity with intelligent analytics.',
    mediaUrl: WHAT_WE_DO_MEDIA.dataEngineering,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Data work with us starts with your business questions. We look at what your team is actually trying to answer — and what's currently painful about answering it — and pick the first pipeline or analytics view worth shipping. Live in your dashboards within weeks, tested against real usage, and built on foundations we can extend as more questions land.",
    learningParagraph:
      "The data tooling landscape shifts constantly — new warehouses, new orchestrators, new analytics patterns. We stay engaged with what's actually working in production and bring the improvements into your setup, so your data platform keeps getting more capable rather than crystallizing on last year's stack.",
    subOfferings: [
      {
        name: 'Data Engineering Pipelines',
        description:
          "Reliable, tested ingestion and transformation pipelines built on modern tooling \u2014 dbt, Airflow, Prefect, or whatever fits your stack.",
      },
      {
        name: 'Data Visualization',
        description:
          "Dashboards that answer the questions your leadership actually asks \u2014 not 200 pre-built widgets, just the handful that matter, built to be trusted.",
      },
      {
        name: 'Real-Time Data Streaming',
        description:
          'Kafka, Kinesis, or Redpanda-based streaming for real-time analytics, alerting, and event-driven applications that need sub-second reactions.',
      },
      {
        name: 'Data Warehousing',
        description:
          "Snowflake, BigQuery, Databricks, or Redshift \u2014 architected to your usage pattern, tuned for cost, and modeled cleanly with dimensional or activity schemas.",
      },
      {
        name: 'AI / ML Services',
        description:
          "ML model development, training pipelines, and MLOps setup \u2014 from feature stores to model registries to production monitoring you can actually trust.",
      },
      {
        name: 'Business Intelligence Programs',
        description:
          "Self-service BI programs that scale beyond a handful of analysts. Governed metrics, semantic layers, and enablement so your team owns their own insights.",
      },
      {
        name: 'Data Governance',
        description:
          "Data lineage, cataloging, quality monitoring, and access controls \u2014 the operational backbone that keeps your data trustworthy at scale.",
      },
      {
        name: 'Conversational Analytics',
        description:
          "Natural-language interfaces on top of your dashboards \u2014 ask a question in plain English, get a chart back. Dynamic reports, on demand, without a ticket.",
      },
    ],
  },
  {
    id: 'marketing-engagement',
    title: 'Marketing and Engagement',
    sidebarTagline:
      'Blend strategy, storytelling, and design to connect with audiences in meaningful, timely, and measurable ways.',
    heroDescription:
      'We blend strategy, design, and media to craft the right story for the right people at the right moment by creating emotional, meaningful connections.',
    mediaUrl: WHAT_WE_DO_MEDIA.marketing,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Marketing work with us starts with your audience and your metrics. We look at what's actually converting, what's leaking budget, and where the story could be sharper. First test lands in weeks — measurable, honest, and either it moves the number or we cut it and try the next one. No long branding exercises before you see whether the work is landing.",
    learningParagraph:
      "Channels shift, audience behaviours evolve, and formats fatigue faster than ever. Our continuous testing across new formats, channels, and creative patterns means your marketing gets fresh experimentation without the risk of you funding it yourself — you get what works, we absorb what didn't.",
    subOfferings: [
      {
        name: 'Brand Strategy & Design',
        description:
          "Positioning, messaging, and visual identity work that pins down how your brand actually shows up \u2014 with the discipline to ship, not endless workshops.",
      },
      {
        name: 'Content Strategy',
        description:
          "Editorial planning, content architecture, and content operations that make your marketing engine actually produce, not just plan.",
      },
      {
        name: 'Marketing Analytics',
        description:
          "Attribution modelling, funnel analysis, and campaign measurement that answers what\u2019s working and where you\u2019re leaking spend.",
      },
      {
        name: 'Digital Campaign Optimization',
        description:
          "Paid, organic, and lifecycle campaigns tested weekly. Real experiments, real winners promoted, real losers cut \u2014 no sacred cows.",
      },
      {
        name: 'Customer Segmentation',
        description:
          "Data-driven segmentation for personalization, retention, and product-led growth. Segments you can actually action, not slideware.",
      },
      {
        name: 'Marketing Automation',
        description:
          "Marketing Cloud, HubSpot, Braze, or Customer.io \u2014 orchestrated for your lifecycle and integrated with your data warehouse, not siloed from it.",
      },
    ],
  },
  {
    id: 'product-platforms',
    title: 'Product and Platforms',
    sidebarTagline:
      'Accelerate growth with next-gen engineering that transforms products, platforms, and possibilities across the lifecycle.',
    heroDescription:
      'We target key phases in the product and brand life cycle to move you forward — collaborating with you on early-stage innovation, growth and continuous improvement, and making next-generational leaps to new platforms and technologies.',
    mediaUrl: WHAT_WE_DO_MEDIA.applicationServices,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Product work with us starts with the user. We look at where your product is friction-heavy today, prototype the smoothest path through it, and validate with real users before we commit to the build. First release lands in weeks, and each subsequent version compounds on what actually worked with your customers, not what we assumed would.",
    learningParagraph:
      "Platform architecture and product patterns evolve constantly. Our ongoing research into new frameworks, developer tools, and design systems means your product keeps getting more capable release over release — without you having to stand up a separate 'engineering excellence' function to keep pace with the industry.",
    subOfferings: [
      {
        name: 'Product Discovery & Strategy',
        description:
          "Structured discovery to figure out what to build first \u2014 user research, market analysis, and rapid prototyping to validate before you commit engineering budget.",
      },
      {
        name: 'UX / UI Design',
        description:
          "Interface design and usability work grounded in real user testing. Design systems that scale, not one-off pretty screens.",
      },
      {
        name: 'Frontend Development',
        description:
          "Modern web frontends in React, Vue, or Svelte \u2014 accessible, performant, and maintainable by your team long after we hand off.",
      },
      {
        name: 'Platform Engineering',
        description:
          "Internal developer platforms that make your engineers faster \u2014 CI/CD, service templates, feature flagging, and observability by default.",
      },
      {
        name: 'Mobile App Development',
        description:
          "Native iOS, Android, or React Native applications with tight ties into your web platforms and backend services.",
      },
      {
        name: 'Design Systems',
        description:
          "Component libraries, tokens, and documentation that let your teams ship consistently. Governed, versioned, and actually adopted \u2014 not shelfware.",
      },
    ],
  },
  {
    id: 'quality-intelligence',
    title: 'Quality Intelligence',
    sidebarTagline:
      'Transform quality engineering with AI-powered automation, predictive testing, and intelligent assurance that improves speed, reliability, and business impact.',
    heroDescription:
      'We transform quality engineering with AI-powered automation, predictive testing, and intelligent assurance that lift the speed, reliability, and business impact of every release.',
    mediaUrl: WHAT_WE_DO_MEDIA.qualityIntelligence,
    ctaLabel: 'Connect with our experts',
    approachParagraph:
      "Quality work with us starts with where your bugs actually live. We look at your incident history, your slowest test suites, and your riskiest user flows — and pick the first automation that would cut the most pain. First working suite lands in weeks; false positives get pruned before your team learns to ignore alerts.",
    learningParagraph:
      "Testing is going through a fundamental shift right now — AI-assisted authoring, predictive test selection, autonomous triage. Our continuous research means your quality function benefits from these advances as they mature, so your test coverage grows while your test suite gets faster and cheaper to run.",
    subOfferings: [
      {
        name: 'AI-Powered Test Automation',
        description:
          "AI-assisted authoring of automated tests \u2014 from spec to running suite in a fraction of the manual authoring time.",
      },
      {
        name: 'Predictive Test Selection',
        description:
          "Machine-learning-based prioritization of your test suite \u2014 run only the tests likely to catch bugs in each change, so builds get fast without losing coverage.",
      },
      {
        name: 'Performance Engineering',
        description:
          "Load testing, capacity planning, and observability wiring so your services don\u2019t fall over the first time real traffic lands.",
      },
      {
        name: 'Continuous Testing',
        description:
          "Testing woven into your CI/CD from day one \u2014 unit, integration, contract, end-to-end \u2014 with clear signal on what a red build actually means.",
      },
      {
        name: 'Test Data Management',
        description:
          "Realistic, PII-safe test data available on demand. No more sharing a production replica in QA, no more waiting a week for someone to seed a fixture.",
      },
      {
        name: 'Autonomous Test Triage',
        description:
          "AI-assisted triage of flaky and failing tests. Root-cause suggestions arrive with the change, not a week later in a post-mortem.",
      },
      {
        name: 'Quality Analytics',
        description:
          "Metrics that actually correlate with production quality \u2014 escape rates, MTTR, test effectiveness \u2014 visualized for your engineering leadership.",
      },
    ],
  },
];

/**
 * O(11) lookup — fine for a short list; if this ever grows past a few
 * dozen entries switch to a Map.
 */
export function getWhatWeDoServiceById(
  id: string | undefined,
): WhatWeDoService | undefined {
  if (!id) return undefined;
  return WHAT_WE_DO_SERVICES.find((s) => s.id === id);
}
